/* ProductReviews — ringkasan rating + daftar ulasan terbit (paginasi, sort, media). */
import React, { useEffect, useState } from "react";
import { Star, BadgeCheck, Play } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function Stars({ value = 0, size = 14, testid }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testid} aria-label={`${value} dari 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= Math.round(value) ? "text-[#F4A261] fill-[#F4A261]" : "text-[#E5E5E5]"} />
      ))}
    </span>
  );
}

export function ReviewMedia({ media }) {
  const [openVideo, setOpenVideo] = useState(false);
  const imgs = media?.images || [];
  const vid = media?.video?.url ? imageUrl(media.video.url) : "";
  if (!imgs.length && !vid) return null;
  return (
    <div className="mt-2" data-testid="review-media">
      <div className="flex gap-2 flex-wrap">
        {imgs.map((m, i) => (
          <a key={m.key || i} href={imageUrl(m.url)} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-[#E5E5E5]" data-testid="review-image">
            <img src={imageUrl(m.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
          </a>
        ))}
        {vid && !openVideo && (
          <button type="button" onClick={() => setOpenVideo(true)} aria-label="Putar video ulasan"
            className="w-16 h-16 rounded-lg border border-[#E5E5E5] bg-black grid place-items-center" data-testid="review-video-thumb">
            <Play size={18} className="text-white" fill="currentColor" />
          </button>
        )}
      </div>
      {vid && openVideo && (
        <video src={vid} controls playsInline preload="metadata" className="mt-2 w-full max-w-md max-h-72 rounded-xl bg-black object-contain" data-testid="review-video-player" />
      )}
    </div>
  );
}

export function ReviewCard({ r }) {
  return (
    <article className="border border-[#E5E5E5] rounded-2xl p-4 bg-white" data-testid="review-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-semibold text-sm" data-testid="review-customer">{r.customer_name || "Pelanggan"}</div>
        <div className="text-xs text-[#5C5C5C]">{r.created_at ? new Date(r.created_at).toLocaleDateString("id-ID") : ""}</div>
      </div>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <Stars value={r.rating} testid="review-rating" />
        {r.verified_purchase && (
          <span className="inline-flex items-center gap-1 text-[11px] text-[#2D6A4F] font-semibold" data-testid="review-verified">
            <BadgeCheck size={12} /> Pembelian Terverifikasi
          </span>
        )}
        {r.purchased_variant?.variant_label && (
          <span className="text-[11px] text-[#5C5C5C]" data-testid="review-variant">Varian dibeli: {r.purchased_variant.variant_label}</span>
        )}
      </div>
      {r.comment && <p className="text-sm mt-2 whitespace-pre-wrap break-words" data-testid="review-comment">{r.comment}</p>}
      <ReviewMedia media={r.media} />
    </article>
  );
}

export function RatingSummary({ summary }) {
  const total = summary?.total_reviews || 0;
  const dist = summary?.distribution || {};
  return (
    <div className="flex gap-6 flex-wrap items-start" data-testid="review-summary">
      <div>
        <div className="text-4xl font-display font-bold" data-testid="review-average">{total ? summary.average_rating : "–"}</div>
        <Stars value={summary?.average_rating || 0} size={16} />
        <div className="text-xs text-[#5C5C5C] mt-1" data-testid="review-total">{total} Ulasan</div>
      </div>
      <div className="flex-1 min-w-[180px] space-y-1">
        {[5, 4, 3, 2, 1].map((s) => (
          <div key={s} className="flex items-center gap-2 text-xs" data-testid={`review-dist-${s}`}>
            <span className="w-6">{s} ★</span>
            <div className="flex-1 h-2 rounded-full bg-[#E5E5E5] overflow-hidden">
              <div className="h-full bg-[#F4A261]" style={{ width: total ? `${((dist[s] || 0) / total) * 100}%` : 0 }} />
            </div>
            <span className="w-6 text-right">{dist[s] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProductReviews({ productId, initialSummary }) {
  const [data, setData] = useState({ items: [], total: 0, summary: initialSummary });
  const [sort, setSort] = useState("newest");
  const [withMedia, setWithMedia] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    api.get(`/products/${productId}/reviews`, { params: { page, limit: 10, sort, with_media: withMedia } })
      .then((r) => setData((d) => ({ ...r.data, items: page === 1 ? r.data.items : [...d.items, ...r.data.items] })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId, page, sort, withMedia]);

  const changeSort = (s) => { setSort(s); setPage(1); };
  return (
    <section id="product-reviews" className="mt-10" data-testid="product-reviews">
      <h2 className="font-display font-bold text-lg mb-4">Ulasan Pelanggan</h2>
      <RatingSummary summary={data.summary} />
      <div className="flex gap-2 mt-4 flex-wrap items-center">
        {[["newest", "Terbaru"], ["highest", "Rating Tertinggi"], ["lowest", "Rating Terendah"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => changeSort(k)} data-testid={`review-sort-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${sort === k ? "brand-border-primary font-semibold" : "border-[#E5E5E5]"}`}>{l}</button>
        ))}
        <label className="text-xs flex items-center gap-1 ml-auto">
          <input type="checkbox" checked={withMedia} onChange={(e) => { setWithMedia(e.target.checked); setPage(1); }} data-testid="review-filter-media" /> Dengan foto/video
        </label>
      </div>
      <div className="mt-4 space-y-3" data-testid="review-list">
        {data.items.map((r) => <ReviewCard key={r.id} r={r} />)}
        {!loading && !data.items.length && <div className="text-sm text-[#5C5C5C]" data-testid="review-empty">Belum ada ulasan untuk produk ini.</div>}
      </div>
      {data.items.length < (data.total || 0) && (
        <Button variant="outline" className="mt-4 rounded-xl" onClick={() => setPage((p) => p + 1)} disabled={loading} data-testid="review-load-more">Muat lebih banyak</Button>
      )}
    </section>
  );
}
